import React, { useState, useEffect, useCallback, useRef } from 'react';
import { copyToClipboard as copyToClipboardUtil } from '../utils/clipboard';

// Общая логика вкладок "Заявки на склад" и "Заказы" маркетплейса.
// Используется как внутри полной Админ-панели (Маркетплейс → Заявки на склад/Заказы),
// так и в урезанной панели "Маркетплейс" в боковом меню.
function AdminMarketRequests({ tgId, adminNickname, activeSubTab, canAct, focusRequestId, focusNonce }) {
  const [marketRequests, setMarketRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);

  // Подсветка конкретной заявки/заказа при переходе из журнала действий по #ID.
  const [highlightId, setHighlightId] = useState(null);
  const focusRef = useRef(null);

  const [mrApproveModal, setMrApproveModal] = useState({ show: false, req: null, lots: '' });
  const [mrRejectModal, setMrRejectModal] = useState({ show: false, reqId: null });
  const [mrRejectReason, setMrRejectReason] = useState('');
  const [mrActionError, setMrActionError] = useState('');

  const [orderRejectModal, setOrderRejectModal] = useState({ show: false, orderId: null });
  const [orderRejectReason, setOrderRejectReason] = useState('');
  const [orderActionError, setOrderActionError] = useState('');

  const showToastMsg = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3000);
  };

  const copyToClipboard = (text) => {
    copyToClipboardUtil(text, () => showToastMsg('Скопировано'), () => showToastMsg('Ошибка копирования', true));
  };

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/market-requests');
      const data = await res.json();
      setMarketRequests(prev => JSON.stringify(prev) !== JSON.stringify(data.requests || []) ? data.requests || [] : prev);
    } catch (err) {
      console.error('Ошибка загрузки заявок маркетплейса', err);
    }
    try {
      const res = await fetch('/api/admin/orders');
      const data = await res.json();
      setOrders(prev => JSON.stringify(prev) !== JSON.stringify(data.orders || []) ? data.orders || [] : prev);
    } catch (err) {
      console.error('Ошибка загрузки заказов', err);
    }
  }, []);

  useEffect(() => {
    fetchData().finally(() => setLoading(false));
    const interval = setInterval(fetchData, 2000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Приход фокуса (клик по #ID в журнале действий): подсвечиваем заявку/заказ и
  // прокручиваем к ней. Через несколько секунд подсветка гаснет.
  useEffect(() => {
    if (!focusNonce || !focusRequestId) return;
    setHighlightId(String(focusRequestId));
    const t = setTimeout(() => setHighlightId(null), 3200);
    return () => clearTimeout(t);
  }, [focusNonce, focusRequestId]);

  useEffect(() => {
    if (highlightId && focusRef.current) {
      focusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightId, marketRequests, orders, activeSubTab]);

  const isFocusHit = (r) => highlightId != null &&
    (String(r.request_id) === highlightId || String(r.order_id) === highlightId || String(r.receipt_id) === highlightId);

  const handleMarketRequestAction = async (requestId, action, qty = '', reason = '') => {
    setMrActionError('');
    if (action === 'reject' && !reason.trim()) {
      setMrActionError('Причина обязательна');
      return;
    }
    if (action === 'approve' && (!qty || qty <= 0)) {
      setMrActionError('Укажите корректное принятое количество товара');
      return;
    }
    try {
      const res = await fetch('/api/admin/market-requests/action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId, admin_nickname: adminNickname, request_id: requestId, action, total_qty: qty, reason })
      });
      if (res.ok) {
        setMrApproveModal({ show: false, req: null, lots: '' });
        setMrRejectModal({ show: false, reqId: null });
        setMrRejectReason('');
        fetchData();
      } else {
        setMrActionError((await res.json()).error);
      }
    } catch (e) { setMrActionError('Ошибка соединения'); }
  };

  const handleOrderAction = async (orderId, action, reason = '') => {
    setOrderActionError('');
    if (action === 'reject' && !reason.trim()) {
      setOrderActionError('Причина обязательна');
      return;
    }
    try {
      const res = await fetch('/api/admin/orders/action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_tg_id: tgId, admin_nickname: adminNickname, order_id: orderId, action, reason })
      });
      if (res.ok) {
        setOrderRejectModal({ show: false, orderId: null });
        setOrderRejectReason('');
        fetchData();
      } else {
        setOrderActionError((await res.json()).error);
      }
    } catch (e) { setOrderActionError('Ошибка соединения'); }
  };

  if (loading) return <div className="card" style={{textAlign: 'center', color: 'var(--text-secondary)'}}>Загрузка...</div>;

  return (
    <div>
      {toast && <div className={`toast ${toast.isError ? 'error' : ''}`}>{toast.msg}</div>}

      {activeSubTab === 'requests' && (
        <div key="mreq-requests" className="tab-fade">
          {marketRequests.length === 0 ? (
            <p style={{textAlign: 'center', color: 'var(--text-secondary)'}}>Заявок пока нет</p>
          ) : (
            marketRequests.map(r => (
              <div key={r.request_id} ref={isFocusHit(r) ? focusRef : null} className={`card${isFocusHit(r) ? ' op-history-highlight' : ''}`}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                  <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                    <div style={{width: '36px', height: '36px', background: 'var(--accent-soft)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>
                      {r.image_url
                        ? <img src={r.image_url} alt="" style={{width: '26px', height: '26px', imageRendering: 'pixelated'}} />
                        : <span style={{fontSize: '18px'}}>📦</span>}
                    </div>
                    <h4 style={{margin: 0}}>{r.item_name}</h4>
                  </div>
                  <span style={{
                    fontSize: '12px', fontWeight: 'bold',
                    color: r.status === 'PENDING' ? 'var(--warning)' : (r.status === 'APPROVED' ? 'var(--success)' : 'var(--danger)')
                  }}>
                    {r.status === 'PENDING' ? '⏳ Ожидает' : (r.status === 'APPROVED' ? '✅ Одобрено' : '❌ Отклонено')}
                  </span>
                </div>
                <p style={{margin: '4px 0', fontSize: '14px'}}><b>Тип:</b> {r.type === 'LISTING' ? 'Выставление товара' : 'Пополнение остатка'}</p>
                <p style={{margin: '4px 0', fontSize: '14px'}}><b>Игрок:</b> {r.mc_nickname}</p>
                {r.cell_number && (
                  <p style={{margin: '4px 0', fontSize: '15px', background: 'var(--accent-soft)', display: 'inline-block', padding: '3px 10px', borderRadius: '8px'}}>
                    📦 <b>Ячейка №{r.cell_number}</b>
                  </p>
                )}
                <p style={{margin: '4px 0', fontSize: '14px'}}><b>Категория:</b> {r.category || '—'}</p>
                <p style={{margin: '4px 0', fontSize: '14px'}}><b>Лотов:</b> {r.unit_qty ? Math.round(r.total_qty / r.unit_qty) : '—'} × {r.unit_qty} шт. = <b>{r.total_qty} шт.</b> всего</p>
                <p style={{margin: '4px 0', fontSize: '14px'}}><b>Цена за лот:</b> {r.price} сц</p>
                {r.description && <p style={{margin: '4px 0', fontSize: '13px', color: 'var(--text-secondary)'}}><b>Описание:</b> {r.description}</p>}
                <p style={{margin: '4px 0', fontSize: '12px', color: 'var(--text-secondary)'}}>Подана: {new Date(r.created_at + 'Z').toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' })}</p>
                <p style={{margin: '4px 0', fontSize: '12px', color: 'var(--warning)'}}>Ожидается на почте: <b>Azon</b></p>

                {r.status === 'REJECTED' && r.reject_reason && (
                  <p style={{margin: '4px 0', fontSize: '13px', color: 'var(--danger)'}}><b>Причина отклонения:</b> {r.reject_reason}</p>
                )}
                {r.status !== 'PENDING' && r.processed_by && (
                  <p style={{margin: '4px 0', fontSize: '12px', color: 'var(--text-secondary)'}}>Обработал: {r.processed_by}</p>
                )}

                {r.status === 'PENDING' && canAct && (
                  <div style={{display: 'flex', gap: '10px', marginTop: '10px'}}>
                    <button className="primary-btn" style={{background: 'var(--success)'}} onClick={() => setMrApproveModal({ show: true, req: r, lots: String(r.unit_qty ? Math.round(r.total_qty / r.unit_qty) : r.total_qty) })}>Одобрить</button>
                    <button className="secondary-btn" style={{color: 'var(--danger)'}} onClick={() => setMrRejectModal({ show: true, reqId: r.request_id })}>Отклонить</button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {activeSubTab === 'orders' && (
        <div key="mreq-orders" className="tab-fade">
          {orders.length === 0 ? (
            <p style={{textAlign: 'center', color: 'var(--text-secondary)'}}>Заказов пока нет</p>
          ) : (
            orders.map(o => {
              const currentCode = o.receipt_id || o.order_id;
              const hit = isFocusHit(o);
              return (
              <div key={o.order_id} ref={hit ? focusRef : null} className={`card${hit ? ' op-history-highlight' : ''}`}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px'}}>
                  <h4 className="selectable-text"
                    style={{margin: '0 0 4px 0', wordBreak: 'break-all', cursor: 'pointer', textDecoration: 'underline', color: 'var(--text-primary)'}}
                    onClick={() => copyToClipboard(currentCode)}
                    title="Нажмите, чтобы скопировать актуальный код"
                  >
                    Заказ {currentCode}
                  </h4>
                  <span style={{
                    fontSize: '12px', fontWeight: 'bold', flexShrink: 0,
                    color: o.status === 'PENDING' ? 'var(--warning)' : (o.status === 'ACCEPTED' ? 'var(--accent-dark)' : (o.status === 'DELIVERED' ? 'var(--success)' : 'var(--danger)'))
                  }}>
                    {o.status === 'PENDING' ? '⏳ В ожидании' : (o.status === 'ACCEPTED' ? '📦 Приняли' : (o.status === 'DELIVERED' ? '✅ Доставлен' : '❌ Отменён'))}
                  </span>
                </div>
                <p style={{margin: '4px 0', fontSize: '14px'}}><b>Покупатель:</b> {o.mc_nickname}</p>
                <p style={{margin: '4px 0', fontSize: '14px'}}><b>Почта доставки:</b> {o.postal_address}</p>
                <p style={{margin: '4px 0', fontSize: '12px', color: 'var(--text-secondary)'}}>Оформлен: {new Date(o.created_at + 'Z').toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' })}</p>

                <div style={{background: 'var(--bg-subtle)', borderRadius: '10px', padding: '8px 10px', margin: '8px 0'}}>
                  {o.items.map((it, idx) => (
                    <div key={idx} style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '3px 0'}}>
                      <span>{it.item_name} × {it.lots * it.unit_qty} шт. {it.cell_number && <b style={{color: 'var(--accent-dark)'}}>[ячейка №{it.cell_number}]</b>} <span style={{color: 'var(--text-secondary)'}}>(от {it.seller_nickname})</span></span>
                      <b>{it.lots * it.price} сц</b>
                    </div>
                  ))}
                </div>

                <p style={{margin: '4px 0', fontSize: '15px', fontWeight: 'bold'}}>Итого: {o.total_price} сц</p>

                {o.status === 'CANCELLED' && o.reject_reason && (
                  <p style={{margin: '4px 0', fontSize: '13px', color: 'var(--danger)'}}><b>Причина отмены:</b> {o.reject_reason}</p>
                )}
                {(o.status === 'DELIVERED' || o.status === 'CANCELLED') && o.processed_by && (
                  <p style={{margin: '4px 0', fontSize: '12px', color: 'var(--text-secondary)'}}>Обработал: {o.processed_by}</p>
                )}

                {(o.status === 'PENDING' || o.status === 'ACCEPTED') && canAct && (
                  <div style={{display: 'flex', gap: '10px', marginTop: '10px'}}>
                    <button className="primary-btn" style={{background: 'var(--success)'}} onClick={() => handleOrderAction(o.order_id, 'approve')}>Собран, отправлен</button>
                    <button className="secondary-btn" style={{color: 'var(--danger)'}} onClick={() => setOrderRejectModal({ show: true, orderId: o.order_id })}>Отклонить</button>
                  </div>
                )}
              </div>
              );
            })
          )}
        </div>
      )}

      {/* Модалки заявок маркетплейса */}
      {mrApproveModal.show && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Одобрение заявки</h3>
            <p style={{fontSize: '14px', color: 'var(--text-secondary)'}}>
              Убедитесь, что товар «{mrApproveModal.req?.item_name}» поступил на склад в заявленном количестве, и укажите фактически принятое количество лотов.
            </p>
            <p style={{fontSize: '13px', color: 'var(--text-secondary)'}}>
              Заявлено: {mrApproveModal.req && mrApproveModal.req.unit_qty ? Math.round(mrApproveModal.req.total_qty / mrApproveModal.req.unit_qty) : ''} лотов
              ({mrApproveModal.req?.unit_qty} шт./лот) = {mrApproveModal.req?.total_qty} шт. всего
            </p>
            <label style={{fontSize: '14px', fontWeight: 'bold'}}>Принято лотов</label>
            <input
              type="number"
              value={mrApproveModal.lots}
              onChange={e => setMrApproveModal({...mrApproveModal, lots: e.target.value})}
            />
            {mrApproveModal.req && mrApproveModal.lots && (
              <p style={{fontSize: '13px', color: 'var(--success)', marginTop: '5px'}}>
                = {(parseInt(mrApproveModal.lots, 10) || 0) * mrApproveModal.req.unit_qty} шт. будет зачислено на склад
              </p>
            )}
            {mrActionError && <div style={{color: 'var(--danger)', fontSize: '14px', marginTop: '5px'}}>{mrActionError}</div>}
            <div className="modal-actions">
              <button className="primary-btn" style={{background: 'var(--success)'}} onClick={() => handleMarketRequestAction(mrApproveModal.req.request_id, 'approve', (parseInt(mrApproveModal.lots, 10) || 0) * mrApproveModal.req.unit_qty)}>Одобрить</button>
              <button className="secondary-btn" onClick={() => { setMrApproveModal({show: false, req: null, lots: ''}); setMrActionError(''); }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {mrRejectModal.show && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Отклонение заявки</h3>
            <p style={{fontSize: '14px', color: 'var(--text-secondary)'}}>Укажите причину отказа. Заявка останется в истории.</p>
            <input
              type="text"
              placeholder="Причина отказа..."
              value={mrRejectReason || ''}
              onChange={e => setMrRejectReason(e.target.value)}
            />
            {mrActionError && <div style={{color: 'var(--danger)', fontSize: '14px', marginTop: '5px'}}>{mrActionError}</div>}
            <div className="modal-actions">
              <button className="primary-btn" style={{background: 'var(--danger)'}} onClick={() => handleMarketRequestAction(mrRejectModal.reqId, 'reject', '', mrRejectReason)}>Отклонить</button>
              <button className="secondary-btn" onClick={() => { setMrRejectModal({show: false, reqId: null}); setMrRejectReason(''); setMrActionError(''); }}>Отмена</button>
            </div>
          </div>
        </div>
      )}

      {orderRejectModal.show && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 style={{marginTop: 0}}>Отклонение заказа</h3>
            <p style={{fontSize: '14px', color: 'var(--text-secondary)'}}>Покупателю будет полностью возвращена сумма заказа. Укажите причину.</p>
            <input
              type="text"
              placeholder="Причина отказа..."
              value={orderRejectReason || ''}
              onChange={e => setOrderRejectReason(e.target.value)}
            />
            {orderActionError && <div style={{color: 'var(--danger)', fontSize: '14px', marginTop: '5px'}}>{orderActionError}</div>}
            <div className="modal-actions">
              <button className="primary-btn" style={{background: 'var(--danger)'}} onClick={() => handleOrderAction(orderRejectModal.orderId, 'reject', orderRejectReason)}>Отклонить</button>
              <button className="secondary-btn" onClick={() => { setOrderRejectModal({show: false, orderId: null}); setOrderRejectReason(''); setOrderActionError(''); }}>Отмена</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminMarketRequests;
